// Catalog.ID Client — this tab's session, told to the desktop dashboard
// ============================================================================
//
// The desktop has two notions of "who is signed in": the dashboard's own
// credential row (which the modules read) and this tab's session (which
// answers every module request). Since 2026-10-08 THIS TAB IS THE AUTHORITY:
// it announces its session and the dashboard follows —
//
//   • the tab logs out            → the dashboard signs out;
//   • the tab logs in             → the dashboard signs in as that account;
//   • the tab is another account  → the dashboard signs the old one out first;
//   • the dashboard's own logout  → the helper pushes `cid-tab-logout` here
//                                   and this tab logs out too (decision 2).
//
// WHAT IS ANNOUNCED, AND WHEN. Only definite states, never "loading":
//   • `signed-in`  — when the shell starts, i.e. the account is signed in AND
//     unlocked here. Carries a one-time LOGIN TICKET when the shell starts
//     right after an interactive login in this page (see LoginTickets).
//   • `signed-out` — when boot finds no session, or the server refused the
//     stored one. Every logout path ends in a reload to `/`, which lands here,
//     so no logout path has to remember to announce.
// Nothing is announced on the unlock screen: a locked tab still holds its
// account, and a lock is not a logout. Nothing is announced on the pre-auth
// routes (register, verify, recovery…) either.
//
// THE CHANNEL is its own socket to the helper (`/api/internal/tab-session`),
// opened at boot on every route, not the module socket (`/api/keygrant/ws`):
// that one exists only after unlock, and a logout must reach a tab sitting on
// its unlock screen too. The helper keeps the last announcement and re-sends
// it to the dashboard whenever that reconnects; this side re-sends it whenever
// IT reconnects (the helper may have restarted).
// ============================================================================

import type { SessionLostInfo } from '../auth/session-lost';

/** The helper's endpoint for this channel (same origin as the page). */
export const TAB_SESSION_PATH = '/api/internal/tab-session';

/**
 * How long a login ticket stays good. The dashboard's follow-up sign-in is a
 * handful of loopback hops and two calls to the Catalog ID node; a minute is
 * ample for that and short enough that a ticket nobody used is gone before
 * anyone could think of using it.
 */
export const LOGIN_TICKET_TTL_MS = 60_000;

/** How long boot waits for the helper's hello before carrying on without it. */
export const BOOT_HELLO_TIMEOUT_MS = 1500;

const RECONNECT_INITIAL_MS = 1000;
const RECONNECT_MAX_MS = 30_000;

export type TabAnnouncement =
    | { state: 'signed-in'; cid: string; loginTicket?: string }
    | { state: 'signed-out' };

// ── Fresh-login tickets ──────────────────────────────────────────────────────

/**
 * The bound on SILENT consent (decision 1, 2026-10-08): when the dashboard
 * follows a login in this tab it signs in itself, and the tab approves that
 * sign-in's OIDC consent without a dialog — but only for a consent that FOLLOWS
 * THIS TAB'S OWN LOGIN. The proof is this ticket:
 *
 *   • minted only when the shell starts right after an interactive login in
 *     this page (password login, a registration, an activation) — never on an
 *     unlock, a reload or an identity switch;
 *   • announced only to the dashboard (the hub hands it to the desktop service
 *     and to nobody else) and echoed back in the desktop's `oidc-consent`;
 *   • good ONCE, for THAT account, within {@link LOGIN_TICKET_TTL_MS}.
 *
 * Anything else — a stale, reused, foreign or absent ticket — gets the dialog,
 * as every consent did before.
 */
export class LoginTickets {
    private current: { cid: string; ticket: string; issuedAt: number; used: boolean } | null = null;

    constructor(private readonly now: () => number = () => Date.now()) {}

    /** A new ticket for `cid`, replacing any earlier one. */
    mint(cid: string): string {
        const bytes = new Uint8Array(24);
        crypto.getRandomValues(bytes);
        const ticket = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
        this.current = { cid, ticket, issuedAt: this.now(), used: false };
        return ticket;
    }

    /**
     * Spend the ticket. True only for the current, unused ticket of `cid`
     * within its window. A wrong presentation does not spend a right one.
     */
    consume(cid: string, ticket: string | undefined): boolean {
        const c = this.current;
        if (!c || !ticket || c.used) return false;
        if (c.cid !== cid || c.ticket !== ticket) return false;
        if (this.now() - c.issuedAt > LOGIN_TICKET_TTL_MS) return false;
        c.used = true;
        return true;
    }

    clear(): void {
        this.current = null;
    }
}

/** The page's tickets. */
export const loginTickets = new LoginTickets();

// ── The channel ──────────────────────────────────────────────────────────────

/** The parts of a WebSocket the channel uses — a seam for tests. */
export interface ChannelSocket {
    readonly readyState: number;
    send(data: string): void;
    close(): void;
    onopen: (() => void) | null;
    onmessage: ((ev: { data: unknown }) => void) | null;
    onclose: (() => void) | null;
}

export interface TabSessionChannelOptions {
    url: string;
    /** The dashboard (through the helper) asks this tab to log out. */
    onLogoutRequested: () => void;
    connect?: (url: string) => ChannelSocket;
    schedule?: (fn: () => void, ms: number) => unknown;
}

const OPEN = 1;

export class TabSessionChannel {
    private socket: ChannelSocket | null = null;
    private stopped = true;
    private delayMs = RECONNECT_INITIAL_MS;
    private latest: TabAnnouncement | null = null;
    private bootWaiter: ((pending: boolean) => void) | null = null;
    private readonly connect: (url: string) => ChannelSocket;
    private readonly schedule: (fn: () => void, ms: number) => unknown;

    constructor(private readonly options: TabSessionChannelOptions) {
        this.connect = options.connect
            ?? ((url) => new WebSocket(url) as unknown as ChannelSocket);
        this.schedule = options.schedule ?? ((fn, ms) => setTimeout(fn, ms));
    }

    start(): void {
        if (!this.stopped) return;
        this.stopped = false;
        this.open();
    }

    stop(): void {
        this.stopped = true;
        const s = this.socket;
        this.socket = null;
        if (s) {
            s.onclose = null;
            try { s.close(); } catch { /* ignore */ }
        }
    }

    /** Say what this tab now holds. Sent at once when connected, else on connect. */
    announce(announcement: TabAnnouncement): void {
        this.latest = announcement;
        this.sendLatest();
    }

    lastAnnouncement(): TabAnnouncement | null {
        return this.latest;
    }

    /**
     * Did the dashboard log out while no Catalog.ID page was listening? Asked
     * at boot, BEFORE a stored session is unlocked, so the tab logs out
     * instead of first answering module requests as the account the dashboard
     * just signed out of. Resolves false when the helper does not answer in
     * time — the push that follows a late hello still logs the tab out.
     */
    logoutPendingAtBoot(timeoutMs: number = BOOT_HELLO_TIMEOUT_MS): Promise<boolean> {
        return new Promise<boolean>(resolve => {
            let settled = false;
            const settle = (pending: boolean): void => {
                if (settled) return;
                settled = true;
                if (this.bootWaiter === waiter) this.bootWaiter = null;
                resolve(pending);
            };
            const waiter = (pending: boolean): void => settle(pending);
            this.bootWaiter = waiter;
            this.schedule(() => settle(false), timeoutMs);
        });
    }

    private open(): void {
        if (this.stopped) return;
        let socket: ChannelSocket;
        try {
            socket = this.connect(this.options.url);
        } catch {
            this.reconnectLater();
            return;
        }
        this.socket = socket;
        socket.onopen = () => {
            this.delayMs = RECONNECT_INITIAL_MS;
            this.sendLatest();
        };
        socket.onmessage = (ev) => this.onMessage(ev.data);
        socket.onclose = () => {
            if (this.socket === socket) this.socket = null;
            this.reconnectLater();
        };
    }

    private reconnectLater(): void {
        if (this.stopped) return;
        const delay = this.delayMs;
        this.delayMs = Math.min(this.delayMs * 2, RECONNECT_MAX_MS);
        this.schedule(() => this.open(), delay);
    }

    private sendLatest(): void {
        const s = this.socket;
        const a = this.latest;
        if (!s || !a || s.readyState !== OPEN) return;
        try {
            s.send(JSON.stringify({ type: 'cid-tab-session', ...a }));
        } catch { /* the reconnect re-sends it */ }
    }

    private onMessage(data: unknown): void {
        let frame: { type?: unknown; logoutPending?: unknown };
        try {
            frame = JSON.parse(String(data)) as typeof frame;
        } catch {
            return;
        }
        if (frame?.type === 'cid-tab-hello') {
            const pending = frame.logoutPending === true;
            const waiter = this.bootWaiter;
            if (waiter) {
                waiter(pending);
                return;
            }
            if (pending) this.options.onLogoutRequested();
            return;
        }
        if (frame?.type === 'cid-tab-logout') {
            this.options.onLogoutRequested();
        }
    }
}

// ── Decisions ────────────────────────────────────────────────────────────────

export type LogoutPushAction = 'log-out' | 're-announce' | 'ignore';

/**
 * What a page does with the dashboard's logout. A signed-in page logs out (no
 * question asked: the member just chose Log out on the dashboard). A page that
 * already told the dashboard it is signed out says so again, which is what
 * clears the helper's "logout owed". A page that never announced anything (a
 * print window, the register page) stays out of it.
 */
export function logoutPushAction(sessionActive: boolean, last: TabAnnouncement | null): LogoutPushAction {
    if (sessionActive) return 'log-out';
    return last?.state === 'signed-out' ? 're-announce' : 'ignore';
}

/**
 * What to announce when the unlock screen gives up a stored session. A token
 * the server refused is a tab that is signed out. "Log in as somebody else"
 * is not: the tab is between accounts, and the next login says which.
 */
export function announcementAfterSessionLoss(info: SessionLostInfo): TabAnnouncement | null {
    return info.reason === 'rejected' ? { state: 'signed-out' } : null;
}
